import React, { useState, useEffect } from "react";
import founder from "../../assets/images/founder.png";
import { FaLocationDot, FaClock, FaCalendarDays } from "react-icons/fa6";

const PrayerTimesCard = () => {
  const [prayerSchedule, setPrayerSchedule] = useState([
    { name: "Fajr", time: "Loading..." },
    { name: "Dhuhr", time: "Loading..." },
    { name: "Asr", time: "Loading..." },
    { name: "Maghrib", time: "Loading..." },
    { name: "Isha", time: "Loading..." },
  ]);
  const [activePrayer, setActivePrayer] = useState(null);
  const [loading, setLoading] = useState(true);

  const today = new Date();
  const formattedDate = today.toLocaleDateString("en-US", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  // Function to convert 24h string (e.g., "17:00") into local Date object for today
  const parsePrayerTime = (timeStr) => {
    const [hours, minutes] = timeStr.split(":");
    const date = new Date();
    date.setHours(parseInt(hours), parseInt(minutes), 0, 0);
    return date;
  };

  // Helper to format 24h time string into standard 12h AM/PM format
  const format12Hour = (timeStr) => {
    if (!timeStr || timeStr.includes("Loading")) return timeStr;
    const [hoursStr, minutesStr] = timeStr.split(":");
    let hours = parseInt(hoursStr);
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    return `${hours}:${minutesStr} ${ampm}`;
  };

  useEffect(() => {
    // Fetch live data for Chittagong, Bangladesh
    // Method 1 is University of Islamic Sciences, Karachi (commonly used in Bangladesh)
    fetch(
      "https://api.aladhan.com/v1/timingsByCity?city=Chittagong&country=Bangladesh&method=1",
    )
      .then((res) => res.json())
      .then((json) => {
        if (json.code === 200 && json.data?.timings) {
          const apiTimings = json.data.timings;

          const schedule = [
            { name: "Fajr", raw: apiTimings.Fajr },
            { name: "Dhuhr", raw: apiTimings.Dhuhr },
            { name: "Asr", raw: apiTimings.Asr },
            { name: "Maghrib", raw: apiTimings.Maghrib },
            { name: "Isha", raw: apiTimings.Isha },
          ];

          // Calculate which prayer interval is currently active
          const now = new Date();
          let currentActive = "Isha"; // Default fallback if it's past Isha or before Fajr

          const fajrTime = parsePrayerTime(apiTimings.Fajr);
          const dhuhrTime = parsePrayerTime(apiTimings.Dhuhr);
          const asrTime = parsePrayerTime(apiTimings.Asr);
          const maghribTime = parsePrayerTime(apiTimings.Maghrib);
          const ishaTime = parsePrayerTime(apiTimings.Isha);

          if (now >= fajrTime && now < dhuhrTime) {
            currentActive = "Fajr";
          } else if (now >= dhuhrTime && now < asrTime) {
            currentActive = "Dhuhr";
          } else if (now >= asrTime && now < maghribTime) {
            currentActive = "Asr";
          } else if (now >= maghribTime && now < ishaTime) {
            currentActive = "Maghrib";
          } else {
            currentActive = "Isha";
          }

          setPrayerSchedule(
            schedule.map((p) => ({
              name: p.name,
              time: format12Hour(p.raw),
            })),
          );
          setActivePrayer(currentActive);
          setLoading(false);
        }
      })
      .catch((err) => {
        console.error("Failed to fetch prayer timings:", err);
        setLoading(false);
      });
  }, []);

  return (
    <div className="mx-auto w-full max-w-2xl bg-white border border-stone-100 shadow-xl rounded-2xl overflow-hidden transform transition-all duration-500 ease-out hover:-translate-y-1.5 hover:shadow-2xl">
      <div className="grid grid-cols-1 md:grid-cols-12">
        {/* Left Segment: Founder Memorial Info */}
        <div className="md:col-span-4 bg-gradient-to-b from-stone-50 to-stone-100/60 p-6 flex flex-col items-center justify-center text-center border-b md:border-b-0 md:border-r border-stone-100">
          <div className="relative mb-3 group">
            <div className="absolute inset-0 bg-emerald-800 rounded-full blur opacity-15" />
            <div className="w-20 h-20 rounded-full overflow-hidden ring-4 ring-white shadow-md relative">
              <img
                src={founder}
                alt="Founder Late Md. Abdul Bari"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          <p className="text-xs font-semibold tracking-wider text-emerald-900 uppercase">
            Founder
          </p>
          <h3 className="text-sm font-bold text-gray-800 leading-tight mt-1">
            Late Md. Abdul Bari
          </h3>
        </div>

        {/* Right Segment: Active Prayer Times Dashboard */}
        <div className="md:col-span-8 p-6 flex flex-col justify-between">
          {/* Metadata Subheading Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-4 border-b border-stone-100">
            <div className="flex items-center gap-2 text-gray-600 text-xs font-medium">
              <FaCalendarDays className="text-emerald-700 w-3.5 h-3.5" />
              <span>{formattedDate}</span>
            </div>
            <div className="flex items-start gap-1.5 text-gray-500 text-xs">
              <FaLocationDot className="text-rose-500 w-3.5 h-3.5 shrink-0 mt-0.5" />
              <span className="leading-tight">
                Road 1, Kunjo Chaya, Bayezid, Ctg.
              </span>
            </div>
          </div>

          {/* Clean Modern Vertical Row Layout for Times */}
          <div className="mt-4 space-y-2">
            {prayerSchedule.map((prayer, index) => {
              const isCurrent = activePrayer === prayer.name;
              return (
                <div
                  key={index}
                  className={`flex items-center justify-between p-3 rounded-xl transition-all duration-200 border ${
                    isCurrent
                      ? "bg-emerald-50/50 border-emerald-100 shadow-sm"
                      : "bg-stone-50/40 border-transparent hover:bg-stone-50 hover:border-stone-100"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span
                      className={`text-xs font-bold w-5 h-5 rounded-md flex items-center justify-center ${
                        isCurrent
                          ? "text-emerald-700 bg-emerald-100"
                          : "text-stone-400 bg-stone-100"
                      }`}
                    >
                      {index + 1}
                    </span>
                    <span
                      className={`font-bold tracking-tight text-sm uppercase flex items-center gap-2 ${
                        isCurrent ? "text-emerald-950" : "text-gray-700"
                      }`}
                    >
                      {prayer.name}
                      {isCurrent && (
                        <span className="relative flex h-2 w-2">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <FaClock
                      className={`w-3.5 h-3.5 ${isCurrent ? "text-emerald-700" : "text-emerald-700/60"}`}
                    />
                    <span
                      className={`font-mono font-bold text-sm ${isCurrent ? "text-emerald-800" : "text-emerald-950"}`}
                    >
                      {prayer.time} (start time)
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrayerTimesCard;
